import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns, Tooltip } from '@bp/ui-components';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { FragmentType, getFragmentData, graphql } from 'client/gql';

const ClassbookAdministrationTableFieldsDocument = graphql(`
  fragment ClassbookAdministrationTableFields on Query {
    absences(options: { sort: [{ startTime: DESC }] }) {
      uuid
      __typename
      owner {
        uuid
        __typename
      }
      reason {
        uuid
        __typename
        reason
      }
      startTime
      endTime
      comment
      excused
    }
    profiles(where: $profilesWhere) {
      uuid
      __typename
      organizationRoles
      displayName
    }
    classes(where: $classesWhere, options: { sort: [{ name: ASC }] }) {
      uuid
      __typename
      membersConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
          }
        }
      }
      shortName
    }
  }
`);

type ClassbookAdministrationTableProps = {
  data: FragmentType<typeof ClassbookAdministrationTableFieldsDocument> | undefined;
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

type ClassbookAdministrationTableType = {
  uuid: string;
  name: string;
  classes: string;
  reason: string;
  from: string;
  until: string;
  comment: string;
  isExcused: boolean;
  ownerUuid: string;
};

export const ClassbookAdministrationTable = ({
  data,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: ClassbookAdministrationTableProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(ClassbookAdministrationTableFieldsDocument, data);

  const tableData: ClassbookAdministrationTableType[] = useMemo(() => {
    const absences = fragment?.absences ?? [];
    const profiles = fragment?.profiles ?? [];
    const classes = fragment?.classes ?? [];

    return (
      absences
        .filter((absence) => {
          const profile = profiles.find((p) => p.uuid === absence.owner.uuid);
          return profile?.organizationRoles.includes('STUDENT');
        })
        .map((absence) => {
          const profile = profiles.find((p) => p.uuid === absence.owner.uuid);
          const profileClasses = classes
            .filter((c) => c.membersConnection.edges.some((e) => e.node.uuid === profile?.uuid))
            .map((c) => c.shortName)
            .sort((a, b) => {
              return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base',
              });
            })
            .join(', ');

          return {
            uuid: absence.uuid,
            name: profile?.displayName ?? '',
            classes: profileClasses ?? '',
            reason: absence.reason?.reason ?? '',
            from: dayjs(absence.startTime).format('DD.MM.YY - HH:mm'),
            until: dayjs(absence.startTime).isSame(absence.endTime, 'day')
              ? dayjs(absence.endTime).format('HH:mm')
              : dayjs(absence.endTime).format('DD.MM.YYYY - HH:mm'),
            comment: absence.comment ?? '',
            isExcused: absence.excused ?? false,
            ownerUuid: absence.owner.uuid,
          };
        }) ?? []
    );
  }, [fragment?.absences, fragment?.classes, fragment?.profiles]);

  const columns: TableColumns<ClassbookAdministrationTableType>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('common.name'),
      size: 225,
    },
    {
      id: 'class',
      accessorKey: 'class',
      header: t('classes.title', { count: 2 }),
      cell: ({ row }) => {
        return (
          <Tooltip
            triggerStyle={{ maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden', width: 'fit-content' }}
            content={row.original.classes}
          >
            {row.original.classes}
          </Tooltip>
        );
      },
      size: 60,
    },
    {
      id: 'reason',
      accessorKey: 'reason',
      header: t('absenceReasons.title', { context: 'short' }),
      size: 175,
    },
    {
      id: 'from',
      accessorKey: 'from',
      header: t('common.from'),
      size: 150,
    },
    {
      id: 'until',
      accessorKey: 'until',
      header: t('common.until'),
      size: 150,
    },
    {
      id: 'comment',
      accessorKey: 'comment',
      header: t('common.comment'),
      size: 250,
    },
    {
      id: 'isExcused',
      accessorKey: 'isExcused',
      header: t('absences.isExcused'),
      type: 'boolean',
      size: 75,
    },
  ];

  return (
    <Table<ClassbookAdministrationTableType>
      data={tableData}
      columns={columns}
      showBorderRadius
      showShadow
      isOnWhite={false}
      showActionBar
      actionBarSettings={{ showAddButton: true }}
      onAddClick={() => onAdd()}
      lastColWidth='68px'
      lastCol={(row) => {
        return (
          <ButtonGroup>
            <Button
              hierarchy='secondary'
              type='button'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
            />
            <Button
              hierarchy='secondary'
              type='button'
              onClick={() => onDelete(row.original.uuid)}
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
            />
          </ButtonGroup>
        );
      }}
      loading={isLoading}
    />
  );
};
